/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `useCorporateIdentityReferenceData` for the Caintra Connect frontend.
 * @module components/AppointmentRegistrationSections/CorporateIdentity/useCorporateIdentityReferenceData
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { getReferenceData } from "../../../logic/companyEdit";
import { fetchSectors } from "../../../logic/register/index.js";
import { resolveSectorClassificationFromTree } from "../../../utils/common";

const useCorporateIdentityReferenceData = ({
  sectorEconomico,
  subSectorSCIAN,
  ramaSCIAN,
}) => {
  const navigate = useNavigate();
  const [businessGroups, setBusinessGroups] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [subSectors, setSubsectors] = useState([]);
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      const [businessGroupsData, sectorsData] = await Promise.all([
        getReferenceData("businessGroups", navigate),
        fetchSectors(navigate),
      ]);
      setBusinessGroups(
        Array.isArray(businessGroupsData) ? businessGroupsData : [],
      );
      setSectors(Array.isArray(sectorsData) ? sectorsData : []);
    };
    fetchData();
  }, [navigate]);

  useEffect(() => {
    if (!sectors.length) {
      setSubsectors([]);
      setBranches([]);
      return;
    }

    const resolved = resolveSectorClassificationFromTree(
      sectors,
      sectorEconomico,
      subSectorSCIAN,
      ramaSCIAN,
    );

    setSubsectors(resolved.subSectors);
    setBranches(resolved.branches);
  }, [sectors, sectorEconomico, subSectorSCIAN, ramaSCIAN]);

  return {
    businessGroups,
    sectors,
    subSectors,
    branches,
    setSubsectors,
    setBranches,
  };
};

export default useCorporateIdentityReferenceData;
